import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { LeverageProductsComponent } from '@zero/leverage-products/feature-leverage-products/web';
import { DisableScrollOnInputDirective } from '@zero/shared/ui-directives';
import { Asset, ExecutionType, InstrumentType, OrderDirection, Quote } from '@zero/shared/util-models';
import { OrderOptionsFormGroup, QuantityType, TrailingType } from '@zero/trade/domain';

@Component({
  selector: 'trade-create-options',
  templateUrl: './trade-create-options.component.html',
  styleUrls: ['./trade-create-options.component.scss'],
  imports: [NgTemplateOutlet, FormsModule, ReactiveFormsModule, DisableScrollOnInputDirective, LeverageProductsComponent, TranslocoPipe],
})
export class TradeCreateOptionsComponent implements OnChanges {
  @Input() public quote?: Quote;
  @Input() public asset?: Asset;
  @Input() public type?: ExecutionType | 'leverage';
  @Input() public direction?: OrderDirection;
  @Input() public form?: FormGroup<OrderOptionsFormGroup>;
  @Input() public hasFraction?: boolean;
  @Input() public mode?: QuantityType;

  @Output() public changeExecType = new EventEmitter<ExecutionType>();
  @Output() public changeMode = new EventEmitter<QuantityType>();

  public displayOptions = true;
  public isLeverageOptionAvailable = false;
  public displayLeverageTooltip = false;
  public isSell = false;
  public isNotSupported = false;

  public ngOnChanges(): void {
    this.isLeverageOptionAvailable = this.asset?.instrumentType === InstrumentType.Share;
    this.displayOptions = this.asset?.instrumentType !== InstrumentType.Crypto;
    this.isSell = this.direction === 'sell';
    this.isNotSupported = this.asset?.disabledExecTypes?.includes(this.type as ExecutionType);
  }

  public toggle = (option: ExecutionType | 'leverage', isActive: boolean): void => {
    if (!isActive || !this.quote || !this.asset) {
      return;
    }

    this.type = option === this.type ? 'market' : option;
    this.type !== 'leverage' && this.changeExecType.emit(this.type);
  };

  public toggleTrailingType = (trailingType: TrailingType): void => {
    switch (trailingType) {
      case 'abs':
        this.form.patchValue({
          trailingAbs: this.#calcAbs(),
          trailingType,
        });
        break;
      case 'rel':
        this.form.patchValue({
          trailingRel: this.#calcRel(),
          trailingType,
        });
        break;
      default:
        throw new Error(`Unknown trailing type: ${trailingType}`);
    }
  };

  public updateRel = (): void => {
    this.form.patchValue({
      trailingRel: this.#calcRel(),
    });
  };

  public updateAbs = (): void => {
    this.form.patchValue({
      trailingAbs: this.#calcAbs(),
    });
  };

  #calcRel = (): number => {
    const abs = this.form.get('trailingAbs').value;
    return abs && this.quote ? (abs / this.quote.bid) * 100 : null;
  };

  #calcAbs = (): number => {
    const rel = this.form.get('trailingRel').value;
    return rel && this.quote ? (this.quote.bid * rel) / 100 : null;
  };
}
